// /components/ui/local-switcher/LanguageBottomSheet.js

'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Image from '@/components/image';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname, useRouter } from '@/i18n/navigation';
import { useSearchParams } from 'next/navigation';
import { event_button_click } from '@/utils/gtm';

export default function LanguageBottomSheet({
  title = '',
  className = '',
  children, // 1. Accept children prop
}) {
  const [open, setOpen] = useState(false);
  const [anim, setAnim] = useState(false);
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations('common');
  const searchParams = useSearchParams();
  const firstBtnRef = useRef(null);

  const options = useMemo(
    () => [
      { code: 'ar', label: 'العربية', flag: '/imgs/layout/ksa-top-header.png' },
      { code: 'en', label: 'English', flag: '/imgs/layout/usa-flag.png' },
    ],
    []
  );

  useEffect(() => {
    if (open) {
      setTimeout(() => setAnim(true), 10);

      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty(
        '--scrollbar-width',
        `${scrollbarWidth}px`
      );

      document.body.classList.add('modal-open');

      setTimeout(() => firstBtnRef.current?.focus(), 50);
      const onKeyDown = (e) => e.key === 'Escape' && closeSheet();
      window.addEventListener('keydown', onKeyDown);

      return () => {
        window.removeEventListener('keydown', onKeyDown);

        document.body.classList.remove('modal-open');
        document.documentElement.style.removeProperty('--scrollbar-width');
      };
    } else {
      setAnim(false);
    }
  }, [open]);
  const closeSheet = () => {
    setAnim(false);
    setTimeout(() => setOpen(false), 200);
  };

  const applyLocale = (newLocale) => {
    if (newLocale === locale) return closeSheet();
    const qs = searchParams.toString();
    const nextPath = qs ? `${pathname}?${qs}` : pathname;
    event_button_click({
      button_name: newLocale === 'en' ? 'English' : 'Arabic',
      button_destination: nextPath,
      button_location: 'side_menu',
    });
    router.push(nextPath, { locale: newLocale });
    closeSheet();
  };

  return (
    <>
      <button
        aria-label='Change language'
        onClick={() => setOpen(true)}
        // 2. Make the button a flex container to layout its children
        className={`flex w-full cursor-pointer items-center justify-between text-left select-none focus-visible:outline-none ${className}`}
      >
        {/* 3. Render children if they exist, otherwise fall back to the title */}
        {children ? (
          children
        ) : (
          <span className='text-[15.8px]'>
            {title || (locale === 'ar' ? 'English' : 'العربية')}
          </span>
        )}
      </button>

      {/* Portal-less overlay (fixed to viewport) */}
      {open && (
        <div
          aria-modal='true'
          role='dialog'
          aria-label='Select language'
          className='fixed inset-0 z-[100]'
        >
          {/* Backdrop */}
          <div
            onClick={closeSheet}
            className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${anim ? 'opacity-100' : 'opacity-0'}`}
          />

          {/* Sheet */}
          <div
            className={`absolute inset-x-0 bottom-0 w-full rounded-t-2xl bg-white p-4 shadow-[0_-8px_24px_rgba(0,0,0,0.12)] transition-transform duration-200 ease-out ${anim ? 'translate-y-0' : 'translate-y-full'}`}
          >
            {/* drag handle */}
            <div className='mx-auto mb-3 h-1.5 w-12 rounded-full bg-gray-200 select-none' />

            <div className='mb-3 flex items-center justify-between select-none'>
              <h2 className='text-lg font-semibold text-[#020202]'>
                {t('Select Language')}
              </h2>
              <button
                onClick={closeSheet}
                aria-label='Close'
                className='rounded-md p-2 text-[16px] text-[#000] hover:bg-gray-100'
              >
                ✕
              </button>
            </div>

            <hr className='mb-5 text-[#F2F2F2]' />

            <div className='space-y-3'>
              {options.map((opt, idx) => {
                const active = opt.code === locale;
                return (
                  <button
                    key={opt.code}
                    ref={idx === 0 ? firstBtnRef : null}
                    onClick={() => applyLocale(opt.code)}
                    className={`flex w-full items-center justify-between rounded-xl border p-3 text-left transition outline-none focus:ring-1 focus:ring-gray-300 ${active ? 'border-red-300 ring-1 ring-red-300/60' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    <span className='flex items-center gap-3'>
                      <span className='block h-6 w-9 overflow-hidden rounded-sm'>
                        <Image
                          src={opt.flag}
                          alt={`${opt.label} flag`}
                          className='h-full w-full object-cover'
                        />
                      </span>
                      <span className='text-[15px] font-medium'>
                        {opt.label}
                      </span>
                    </span>
                    {active && (
                      <span aria-hidden className='text-sm'>
                        ✓
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
